import { useState, useEffect, useRef, useMemo } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Minus, Camera, Play, Pause, ArrowClockwise, FloppyDisk, X, Trash, Check, TennisBall } from "@phosphor-icons/react";

const formatTimer = (s) => {
  const sec = Math.max(0, Number(s) || 0);
  const m = Math.floor(sec / 60).toString().padStart(2, "0");
  const r = (sec % 60).toString().padStart(2, "0");
  return `${m}:${r}`;
};

// Tennis point advancement: null→"15"→"30"→"40"→game (with deuce/AD logic).
const _next = { "": "15", "0": "15", "15": "30", "30": "40" };
function advanceTennis(cur, opp) {
  // cur = who scored the point, opp = opponent's current
  if (cur === "" || cur === "0") return { cur: "15", opp, game: false };
  if (cur === "15") return { cur: "30", opp, game: false };
  if (cur === "30") return { cur: "40", opp, game: false };
  if (cur === "40") {
    // deuce/AD logic
    if (opp === "40") return { cur: "AD", opp: "40", game: false };
    if (opp === "AD") return { cur: "40", opp: "40", game: false }; // opponent had AD → back to deuce
    return { cur: "0", opp: "0", game: true };
  }
  if (cur === "AD") return { cur: "0", opp: "0", game: true };
  return { cur, opp, game: false };
}

const PlayerSelect = ({ players, value, onChange, placeholder, testId }) => {
  const key = value?.user_id || value?.name || "__none__";
  return (
    <Select value={key} onValueChange={(v) => {
      if (v === "__none__") return onChange(null);
      const p = players.find((pp) => (pp.user_id || pp.name) === v);
      onChange(p || null);
    }}>
      <SelectTrigger className="h-9 bg-[#0A0A0A] border-white/15 text-xs" data-testid={testId}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="bg-[#0A0A0A] border-white/15 max-h-60">
        <SelectItem value="__none__">— vacio —</SelectItem>
        {players.map((p, i) => (
          <SelectItem key={(p.user_id || p.name) + i} value={p.user_id || p.name}>
            {p.name}{p.is_guest ? " (inv)" : ""}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export default function RetaScoreboard({ reta, courtIdx, scoreboard, canEdit, onClose, onDelete, onSaved }) {
  const court = (reta.courts || [])[courtIdx] || {};
  const courtPlayers = court.players || [];
  const existing = scoreboard || {};
  const sbId = existing.id;
  const sbNumber = existing.number || 1;
  const sbRound = existing.round || 1;
  // Priorizar el roster de la ronda; fallback al roster general de la cancha
  const roundRoster = (court.round_rosters || {})[String(sbRound)] || [];
  const players = roundRoster.length ? roundRoster : courtPlayers;

  const [lineup, setLineup] = useState({
    a1: existing.lineup?.a1 || null,
    a2: existing.lineup?.a2 || null,
    b1: existing.lineup?.b1 || null,
    b2: existing.lineup?.b2 || null,
  });
  const [scoreA, setScoreA] = useState(existing.score_a ?? 0);
  const [scoreB, setScoreB] = useState(existing.score_b ?? 0);
  // Persistent server-side timer: on Start we save `timer_started_at` +
  // `timer_running=true`; on Pause we accumulate to `timer_seconds` and clear
  // `timer_started_at`. On mount we hydrate elapsed based on server state.
  const _initialElapsed = () => {
    const base = Number(existing.timer_seconds || 0);
    if (existing.timer_running && existing.timer_started_at) {
      const t0 = new Date(existing.timer_started_at).getTime();
      if (!isNaN(t0)) return base + Math.max(0, Math.floor((Date.now() - t0) / 1000));
    }
    return base;
  };
  const [timerSec, setTimerSec] = useState(_initialElapsed());
  const [running, setRunning] = useState(!!existing.timer_running);
  // Tennis interactive scoreboard state (15/30/40/AD + games + sets)
  const [tScoreA, setTScoreA] = useState(existing.tennis_score_a ?? "0");
  const [tScoreB, setTScoreB] = useState(existing.tennis_score_b ?? "0");
  const [tGamesA, setTGamesA] = useState(existing.tennis_games_a ?? 0);
  const [tGamesB, setTGamesB] = useState(existing.tennis_games_b ?? 0);
  const [tSetsA, setTSetsA] = useState(existing.tennis_sets_a ?? 0);
  const [tSetsB, setTSetsB] = useState(existing.tennis_sets_b ?? 0);
  const [tHistory, setTHistory] = useState(existing.tennis_history || []);
  const [photoB64, setPhotoB64] = useState(existing.photo_b64 || "");
  const [status, setStatus] = useState(existing.status || "en_curso");
  const [saving, setSaving] = useState(false);
  const intRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (running) {
      intRef.current = setInterval(() => setTimerSec((s) => s + 1), 1000);
    }
    return () => { if (intRef.current) clearInterval(intRef.current); };
  }, [running]);

  // Persist Start / Pause so the timer keeps counting even if the user closes
  // the app. On Start we push `timer_started_at=now`; on Pause we accumulate.
  const toggleTimer = async () => {
    if (!canEdit || !sbId) return;
    const willRun = !running;
    if (willRun) {
      setRunning(true);
      try {
        await api.put(`/retas/multi/${reta.id}/courts/${courtIdx}/scoreboards/${sbId}`, {
          timer_running: true,
          timer_started_at: new Date().toISOString(),
          timer_seconds: timerSec,
        });
      } catch { /* soft-fail */ }
    } else {
      setRunning(false);
      try {
        await api.put(`/retas/multi/${reta.id}/courts/${courtIdx}/scoreboards/${sbId}`, {
          timer_running: false,
          timer_started_at: "",
          timer_seconds: timerSec,
        });
      } catch { /* soft-fail */ }
    }
  };
  const resetTimer = async () => {
    setRunning(false);
    setTimerSec(0);
    if (canEdit && sbId) {
      try {
        await api.put(`/retas/multi/${reta.id}/courts/${courtIdx}/scoreboards/${sbId}`, {
          timer_running: false,
          timer_started_at: "",
          timer_seconds: 0,
        });
      } catch { /* soft-fail */ }
    }
  };

  // ─── Tennis interactive scoreboard handlers ─────────────────────────────
  const persistTennis = async (payload) => {
    if (!canEdit || !sbId) return;
    try {
      await api.put(`/retas/multi/${reta.id}/courts/${courtIdx}/scoreboards/${sbId}`, payload);
    } catch { /* soft-fail — will save on manual "Guardar" */ }
  };

  const addTennisPoint = (side) => {
    if (!canEdit) return;
    const isA = side === "a";
    const cur = isA ? tScoreA : tScoreB;
    const opp = isA ? tScoreB : tScoreA;
    const res = advanceTennis(cur, opp);
    let nextGamesA = tGamesA, nextGamesB = tGamesB;
    let nextSetsA = tSetsA, nextSetsB = tSetsB;
    if (res.game) {
      if (isA) nextGamesA += 1; else nextGamesB += 1;
      // set won at 6 games with ≥2 lead OR 7 games
      const setWonA = nextGamesA >= 6 && nextGamesA - nextGamesB >= 2 || nextGamesA === 7;
      const setWonB = nextGamesB >= 6 && nextGamesB - nextGamesA >= 2 || nextGamesB === 7;
      if (setWonA) { nextSetsA += 1; nextGamesA = 0; nextGamesB = 0; }
      if (setWonB) { nextSetsB += 1; nextGamesA = 0; nextGamesB = 0; }
    }
    const nextTA = isA ? res.cur : res.opp;
    const nextTB = isA ? res.opp : res.cur;
    setTScoreA(nextTA);
    setTScoreB(nextTB);
    setTGamesA(nextGamesA);
    setTGamesB(nextGamesB);
    setTSetsA(nextSetsA);
    setTSetsB(nextSetsB);
    const history = [...tHistory, { side, at: new Date().toISOString() }].slice(-40);
    setTHistory(history);
    persistTennis({
      tennis_score_a: nextTA,
      tennis_score_b: nextTB,
      tennis_games_a: nextGamesA,
      tennis_games_b: nextGamesB,
      tennis_sets_a: nextSetsA,
      tennis_sets_b: nextSetsB,
      tennis_history: history,
    });
  };

  const undoTennis = () => {
    if (!canEdit || !tHistory.length) return;
    // Simple approach: replay all but last
    const prev = tHistory.slice(0, -1);
    let a = "0", b = "0", ga = 0, gb = 0, sa = 0, sb = 0;
    for (const step of prev) {
      const isA = step.side === "a";
      const cur = isA ? a : b;
      const opp = isA ? b : a;
      const r = advanceTennis(cur, opp);
      let nga = ga, ngb = gb, nsa = sa, nsb = sb;
      if (r.game) {
        if (isA) nga += 1; else ngb += 1;
        const wA = nga >= 6 && nga - ngb >= 2 || nga === 7;
        const wB = ngb >= 6 && ngb - nga >= 2 || ngb === 7;
        if (wA) { nsa += 1; nga = 0; ngb = 0; }
        if (wB) { nsb += 1; nga = 0; ngb = 0; }
      }
      a = isA ? r.cur : r.opp;
      b = isA ? r.opp : r.cur;
      ga = nga; gb = ngb; sa = nsa; sb = nsb;
    }
    setTScoreA(a); setTScoreB(b);
    setTGamesA(ga); setTGamesB(gb);
    setTSetsA(sa); setTSetsB(sb);
    setTHistory(prev);
    persistTennis({
      tennis_score_a: a, tennis_score_b: b,
      tennis_games_a: ga, tennis_games_b: gb,
      tennis_sets_a: sa, tennis_sets_b: sb,
      tennis_history: prev,
    });
  };

  const resetTennis = () => {
    if (!canEdit) return;
    setTScoreA("0"); setTScoreB("0");
    setTGamesA(0); setTGamesB(0);
    setTSetsA(0); setTSetsB(0);
    setTHistory([]);
    persistTennis({
      tennis_score_a: "0", tennis_score_b: "0",
      tennis_games_a: 0, tennis_games_b: 0,
      tennis_sets_a: 0, tennis_sets_b: 0,
      tennis_history: [],
    });
  };

  // Auto-prefill lineup with first 4 unassigned players if pizarron is empty
  useEffect(() => {
    setLineup((l) => {
      if (l.a1 || l.a2 || l.b1 || l.b2) return l;
      const slots = ["a1", "a2", "b1", "b2"];
      const next = { ...l };
      players.slice(0, 4).forEach((p, i) => { next[slots[i]] = p; });
      return next;
    });
    // eslint-disable-next-line
  }, [reta.id, courtIdx, sbId]);

  const usedKeys = useMemo(() => {
    const set = new Set();
    Object.values(lineup).forEach((p) => { if (p) set.add(p.user_id || p.name); });
    return set;
  }, [lineup]);

  const availFor = (slot) => {
    const current = lineup[slot];
    return players.filter((p) => {
      const k = p.user_id || p.name;
      if (current && (current.user_id || current.name) === k) return true;
      return !usedKeys.has(k);
    });
  };

  const onScoreChange = (side, delta) => {
    if (side === "a") setScoreA((v) => Math.max(0, Math.min(10, v + delta)));
    else setScoreB((v) => Math.max(0, Math.min(10, v + delta)));
  };

  const onPickPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1_800_000) { toast.error("Foto muy grande (max 1.8MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setPhotoB64(reader.result);
    reader.readAsDataURL(f);
  };

  const save = async (markFinished = false) => {
    if (!canEdit) { toast.error("Sin permiso para guardar"); return; }
    if (!sbId) { toast.error("ID del pizarron no encontrado"); return; }
    setSaving(true);
    try {
      const finalStatus = markFinished ? "finalizado" : status;
      const res = await api.put(`/retas/multi/${reta.id}/courts/${courtIdx}/scoreboards/${sbId}`, {
        lineup,
        score_a: scoreA,
        score_b: scoreB,
        timer_seconds: timerSec,
        photo_b64: photoB64 || "",
        status: finalStatus,
      });
      setStatus(finalStatus);
      toast.success(markFinished ? `Reta #${sbNumber} finalizada` : "Pizarron guardado");
      onSaved?.(res.data.scoreboard);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const ScoreBox = ({ side, value, color, testId }) => (
    <div className={`flex flex-col items-center bg-[#0A0A0A] border-2 ${color} px-3 py-2 rounded-md`} data-testid={testId}>
      <div className="text-[9px] tracking-[0.2em] uppercase text-gray-400 mb-1">Pareja {side.toUpperCase()}</div>
      <div className="font-['Barlow_Condensed'] text-6xl font-black leading-none" data-testid={`${testId}-value`}>{value}</div>
      <div className="flex gap-1 mt-2">
        <Button size="sm" variant="outline" onClick={() => onScoreChange(side, -1)} disabled={!canEdit || value <= 0} className="h-8 w-8 p-0 border-red-500/40 text-red-400 hover:bg-red-500/10" data-testid={`${testId}-minus`}>
          <Minus size={14} weight="bold" />
        </Button>
        <Button size="sm" variant="outline" onClick={() => onScoreChange(side, 1)} disabled={!canEdit || value >= 10} className="h-8 w-8 p-0 border-[#CCFF00]/40 text-[#CCFF00] hover:bg-[#CCFF00]/10" data-testid={`${testId}-plus`}>
          <Plus size={14} weight="bold" />
        </Button>
      </div>
    </div>
  );

  return (
    <div className="mt-3 p-4 bg-gradient-to-br from-[#070707] to-[#0A0A0A] border border-[#CCFF00]/20 rounded-md space-y-4" data-testid={`scoreboard-${reta.id}-${courtIdx}-${sbNumber}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#CCFF00] font-bold">Pizarron Round-Robin</span>
            <span className={`px-1.5 py-0.5 text-[9px] uppercase font-bold rounded border ${status === "finalizado" ? "bg-green-500/15 text-green-400 border-green-500/30" : "bg-blue-500/15 text-blue-400 border-blue-500/30"}`}>
              {status === "finalizado" ? "Finalizada" : "En curso"}
            </span>
          </div>
          <div className="text-sm font-bold text-white">{court.name} · Ronda {existing.round || 1} · Reta #{sbNumber}</div>
        </div>
        <div className="flex items-center gap-2">
          <div className="px-3 py-1.5 bg-black border border-[#CCFF00]/30 rounded font-mono text-base text-[#CCFF00]" data-testid={`scoreboard-timer-${reta.id}-${courtIdx}`}>
            ⏱ {formatTimer(timerSec)}
          </div>
          <Button size="sm" variant="outline" onClick={toggleTimer} disabled={!canEdit} className="h-8 border-white/15 text-[10px] uppercase" data-testid={`scoreboard-toggle-timer-${reta.id}-${courtIdx}`}>
            {running ? <><Pause size={12} className="mr-1" /> Pausar</> : <><Play size={12} className="mr-1" /> Start</>}
          </Button>
          <Button size="sm" variant="outline" onClick={resetTimer} disabled={!canEdit} className="h-8 w-8 p-0 border-white/15" title="Reset cronometro">
            <ArrowClockwise size={12} />
          </Button>
          {onDelete && (
            <Button size="sm" variant="outline" onClick={onDelete} className="h-8 w-8 p-0 border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid={`scoreboard-delete-${reta.id}-${courtIdx}-${sbNumber}`} title="Eliminar pizarron">
              <Trash size={12} />
            </Button>
          )}
          {onClose && (
            <Button size="sm" variant="outline" onClick={onClose} className="h-8 w-8 p-0 border-white/15" data-testid={`scoreboard-close-${reta.id}-${courtIdx}`}>
              <X size={12} />
            </Button>
          )}
        </div>
      </div>

      {/* Lineups + Score */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-start">
        <div className="space-y-2">
          <div className="text-[10px] tracking-[0.2em] uppercase text-cyan-400 font-bold">Pareja A</div>
          <PlayerSelect players={availFor("a1")} value={lineup.a1} onChange={(p) => setLineup({ ...lineup, a1: p })} placeholder="Jugador A1" testId={`scoreboard-${reta.id}-${courtIdx}-${sbNumber}-a1`} />
          <PlayerSelect players={availFor("a2")} value={lineup.a2} onChange={(p) => setLineup({ ...lineup, a2: p })} placeholder="Jugador A2" testId={`scoreboard-${reta.id}-${courtIdx}-${sbNumber}-a2`} />
        </div>
        <div className="flex items-center justify-center gap-2 md:gap-3 py-2">
          <ScoreBox side="a" value={scoreA} color="border-cyan-500/40" testId={`scoreboard-score-a-${reta.id}-${courtIdx}-${sbNumber}`} />
          <div className="font-['Barlow_Condensed'] text-3xl text-gray-600 font-black">·</div>
          <ScoreBox side="b" value={scoreB} color="border-orange-500/40" testId={`scoreboard-score-b-${reta.id}-${courtIdx}-${sbNumber}`} />
        </div>
        <div className="space-y-2">
          <div className="text-[10px] tracking-[0.2em] uppercase text-orange-400 font-bold text-right">Pareja B</div>
          <PlayerSelect players={availFor("b1")} value={lineup.b1} onChange={(p) => setLineup({ ...lineup, b1: p })} placeholder="Jugador B1" testId={`scoreboard-${reta.id}-${courtIdx}-${sbNumber}-b1`} />
          <PlayerSelect players={availFor("b2")} value={lineup.b2} onChange={(p) => setLineup({ ...lineup, b2: p })} placeholder="Jugador B2" testId={`scoreboard-${reta.id}-${courtIdx}-${sbNumber}-b2`} />
        </div>
      </div>

      {/* Interactive Tennis Scoreboard — 15/30/40 + games + sets */}
      <div className="border border-[#CCFF00]/20 rounded-md bg-gradient-to-br from-black to-[#0A0A0A] p-3 space-y-3" data-testid={`tennis-scoreboard-${reta.id}-${courtIdx}-${sbNumber}`}>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#CCFF00] font-bold flex items-center gap-1">
            <TennisBall size={12} weight="fill" /> Marcador interactivo · 15 · 30 · 40 · Juego
          </div>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" onClick={undoTennis} disabled={!canEdit || !tHistory.length} className="h-7 border-white/15 text-[9px] uppercase" data-testid={`tennis-undo-${reta.id}-${courtIdx}-${sbNumber}`}>
              <ArrowClockwise size={10} className="mr-1" /> Deshacer
            </Button>
            <Button size="sm" variant="outline" onClick={resetTennis} disabled={!canEdit} className="h-7 border-red-500/30 text-red-400 text-[9px] uppercase" data-testid={`tennis-reset-${reta.id}-${courtIdx}-${sbNumber}`}>
              Reset
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 md:gap-4">
          {/* A */}
          <div className="text-center space-y-1">
            <div className="text-[9px] uppercase tracking-widest text-cyan-400 font-bold">Pareja A</div>
            <div className="font-['Barlow_Condensed'] text-6xl md:text-7xl font-black text-cyan-300 leading-none" data-testid={`tennis-score-a-${reta.id}-${courtIdx}-${sbNumber}`}>{tScoreA}</div>
            <div className="flex justify-center gap-3 text-[10px] text-gray-400 font-mono">
              <span data-testid={`tennis-games-a-${reta.id}-${courtIdx}-${sbNumber}`}>Games <b className="text-white">{tGamesA}</b></span>
              <span data-testid={`tennis-sets-a-${reta.id}-${courtIdx}-${sbNumber}`}>Sets <b className="text-white">{tSetsA}</b></span>
            </div>
            <Button size="sm" onClick={() => addTennisPoint("a")} disabled={!canEdit} className="w-full mt-1 bg-cyan-500 hover:bg-cyan-400 text-black font-bold uppercase text-xs h-9" data-testid={`tennis-plus-a-${reta.id}-${courtIdx}-${sbNumber}`}>
              +1 Punto A
            </Button>
          </div>
          <div className="font-['Barlow_Condensed'] text-3xl text-gray-700 font-black text-center">vs</div>
          {/* B */}
          <div className="text-center space-y-1">
            <div className="text-[9px] uppercase tracking-widest text-orange-400 font-bold">Pareja B</div>
            <div className="font-['Barlow_Condensed'] text-6xl md:text-7xl font-black text-orange-300 leading-none" data-testid={`tennis-score-b-${reta.id}-${courtIdx}-${sbNumber}`}>{tScoreB}</div>
            <div className="flex justify-center gap-3 text-[10px] text-gray-400 font-mono">
              <span data-testid={`tennis-games-b-${reta.id}-${courtIdx}-${sbNumber}`}>Games <b className="text-white">{tGamesB}</b></span>
              <span data-testid={`tennis-sets-b-${reta.id}-${courtIdx}-${sbNumber}`}>Sets <b className="text-white">{tSetsB}</b></span>
            </div>
            <Button size="sm" onClick={() => addTennisPoint("b")} disabled={!canEdit} className="w-full mt-1 bg-orange-500 hover:bg-orange-400 text-black font-bold uppercase text-xs h-9" data-testid={`tennis-plus-b-${reta.id}-${courtIdx}-${sbNumber}`}>
              +1 Punto B
            </Button>
          </div>
        </div>
        <p className="text-[10px] text-gray-500 leading-tight text-center">
          Los cambios del marcador se guardan al instante · Deuce & Ventaja automáticos · Set a 6 games con ≥2 de diferencia o 7-5
        </p>
      </div>

      {/* Photo */}
      <div className="space-y-2">
        <div className="text-[10px] tracking-[0.2em] uppercase text-gray-400 font-bold">Foto del pizarron fisico (opcional)</div>
        <div className="flex items-start gap-3 flex-wrap">
          <input ref={fileRef} type="file" accept="image/*" onChange={onPickPhoto} className="hidden" data-testid={`scoreboard-photo-input-${reta.id}-${courtIdx}-${sbNumber}`} />
          <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={!canEdit} className="border-white/15 text-[10px] uppercase h-8" data-testid={`scoreboard-photo-pick-${reta.id}-${courtIdx}-${sbNumber}`}>
            <Camera size={12} className="mr-1" /> {photoB64 ? "Cambiar foto" : "Subir foto"}
          </Button>
          {photoB64 && (
            <div className="relative">
              <img src={photoB64} alt="Pizarron" className="max-h-32 rounded border border-white/10" data-testid={`scoreboard-photo-preview-${reta.id}-${courtIdx}-${sbNumber}`} />
              {canEdit && (
                <button onClick={() => setPhotoB64("")} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600">
                  <X size={10} />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t border-white/5 flex-wrap">
        <Button onClick={() => save(false)} disabled={!canEdit || saving} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide text-xs" data-testid={`scoreboard-save-${reta.id}-${courtIdx}-${sbNumber}`}>
          <FloppyDisk size={14} className="mr-1" /> {saving ? "Guardando..." : "Guardar"}
        </Button>
        {status !== "finalizado" && (
          <Button onClick={() => save(true)} disabled={!canEdit || saving} variant="outline" className="border-green-500/40 text-green-400 hover:bg-green-500/10 font-bold uppercase tracking-wide text-xs" data-testid={`scoreboard-finish-${reta.id}-${courtIdx}-${sbNumber}`}>
            <Check size={14} className="mr-1" /> Finalizar reta
          </Button>
        )}
      </div>

      {!canEdit && (
        <div className="text-[10px] text-gray-500 italic">Solo staff o jugadores asignados a esta cancha pueden modificar el pizarron.</div>
      )}
    </div>
  );
}
